/**
 * src/data/helpData.js
 *
 * Embedded help content for Orrith, FAQ, guides, and categories.
 * No API dependency; renders from memory for zero-latency offline support.
 */

const content = {
  en: {
    categories: [
      { id: "getting-started", label: "Getting Started", desc: "Create your account, explore features, and start using Orrith - Cycle of Certainty." },
      { id: "marketplace", label: "Farm Marketplace", desc: "List produce in RWF, make offers, negotiate in chat, and manage sales." },
      { id: "market-data", label: "FarmPulse Data", desc: "Crowdsourced crop prices across 30 Rwandan districts with trend charts." },
      { id: "impact", label: "FarmMark Impact", desc: "Earn Impact Points, climb leaderboards, and track your CO2 savings." },
      { id: "account", label: "Account & Security", desc: "Sign in with email, Google, or phone. Export data, control privacy, delete account." },
    ],

    faq: [
      {
        id: "faq-1",
        question: "How do I list a product for sale on Farm?",
        answer: "Go to Sell Now, choose one of 24 crop categories, upload up to 3 photos, set a price in RWF and quantity in kg, then pick an expiry date. Your listing appears immediately on the Farm marketplace with a live countdown timer.",
        category: "marketplace",
      },
      {
        id: "faq-2",
        question: "How do offers and buying work?",
        answer: "Open any product and tap Make Offer to propose a price in RWF. This creates a private chat with the seller where you can negotiate. If the seller accepts, the product is marked as sold and you coordinate pickup or delivery through messages.",
        category: "marketplace",
      },
      {
        id: "faq-3",
        question: "What is the Nearby filter on Farm?",
        answer: "The Nearby button uses your phone's GPS to show only products listed within 5 km of your current location. You need to grant location permission when prompted. You can also sort results by distance using the Closest sort option.",
        category: "marketplace",
      },
      {
        id: "faq-4",
        question: "Can I save a product to buy later?",
        answer: "Yes, tap the heart icon on any product card to add it to your Wishlist. You can view all saved items from the Wishlist page. Removing an item shows an undo option for 6 seconds in case you change your mind.",
        category: "marketplace",
      },
      {
        id: "faq-5",
        question: "What is FarmPulse and how do prices get there?",
        answer: "FarmPulse is a crowdsourced market intelligence dashboard covering 29 crops across all 30 districts of Rwanda. Users submit the prices they see at local markets, and a price becomes validated when 5 independent people report similar amounts within a 7-day window.",
        category: "market-data",
      },
      {
        id: "faq-9",
        question: "How do I contribute a price report to FarmPulse?",
        answer: "Open FarmPulse and tap Contribute. Select a crop category and crop name, enter the price with a unit (kg, ton, bag, bundle, etc.), and choose your district. You earn 1 Impact Point per submission and can report once per crop per day.",
        category: "market-data",
      },
      {
        id: "faq-10",
        question: "What do the market signals in FarmPulse mean?",
        answer: "FarmPulse calculates signals like Strong Buy, Buy, Hold, or Avoid based on 7-day, 30-day, and 90-day price trends, supply shifts, and seasonal patterns. Each crop also shows sparkline charts with price history and 1 to 6-month forecasts with confidence scores.",
        category: "market-data",
      },
      {
        id: "faq-11",
        question: "How do Impact Points work in FarmMark?",
        answer: "You earn 5 IP for listing a product, 1 IP for each FarmPulse price report, 1 IP for a planting report, and 3 IP per star when rating another user. Your badge level goes from Seedling to Sprout, Cultivator, Harvester, Guardian, and Legendary Steward.",
        category: "impact",
      },
      {
        id: "faq-9",
        question: "What leaderboards does FarmMark have?",
        answer: "FarmMark has two leaderboards: Best Sellers ranked by product listings and Price Scouts ranked by FarmPulse submissions. Each can be filtered by week, month, or all-time. There is also a Carbon Saved tab showing your CO2 impact.",
        category: "impact",
      },
      {
        id: "faq-10",
        question: "What is NorthStar?",
        answer: "NorthStar is Orrith - Cycle of Certainty's live mission and impact page. It shows real-time counts of community members, products listed, and impact events pulled directly from the network. It also has a live feed showing recent ratings as they happen.",
        category: "getting-started",
      },
      {
        id: "faq-11",
        question: "How do messages and chats work?",
        answer: "Chats are created automatically when you make an offer on a product. Your Inbox has two tabs: Messages for conversations and Notifications for system alerts. You can search conversations, see unread indicators, and the other person's typing status.",
        category: "marketplace",
      },
      {
        id: "faq-12",
        question: "What sign-in methods does Orrith support?",
        answer: "You can sign in with email and password, username and password, phone number and password, or your Google account. Signup includes optional phone verification via OTP and a location picker covering all provinces, districts, sectors, and cells in Rwanda.",
        category: "account",
      },
      {
        id: "faq-13",
        question: "How do I change the app language?",
        answer: "Go to Settings, open General settings, and select English or Kinyarwanda from the Language control. The change applies immediately across the app. You can also switch language before signing in using the language selector on login.",
        category: "account",
      },
      {
        id: "faq-14",
        question: "How do I export or delete my data?",
        answer: "In Settings under Privacy and Data, tap Export Data to download your account data as a JSON file. To permanently delete your account, open Danger Zone and select Delete Account. Deletion is permanent and cannot be undone.",
        category: "account",
      },
      {
        id: "faq-15",
        question: "What privacy controls are available?",
        answer: "In Settings under Privacy and Data, you can export your data, reset preferences, and start full account deletion. For public profile visibility fields, open Edit Profile and profile privacy controls.",
        category: "account",
      },
      {
        id: "faq-16",
        question: "How do I edit or hide a product after posting?",
        answer: "Open your product and tap the three-dot menu. Choose Edit to update details, photos, or price. Choose Hide to make it private so only you can see it, or Delete to remove it permanently. You can also manage all your listings from your profile page.",
        category: "marketplace",
      },
      {
        id: "faq-17",
        question: "How does the expiration system work for products?",
        answer: "When you list a product, Orrith - Cycle of Certainty calculates a shelf life based on your crop category, product state (fresh, dried, frozen, etc.), and storage condition. A live countdown appears on your listing showing exactly how much time is left. Expired products are automatically hidden from the marketplace.",
        category: "marketplace",
      },
      {
        id: "faq-18",
        question: "How is CO2 impact calculated in FarmMark?",
        answer: "FarmMark estimates 2.5 kg of CO2 saved for every kilogram of food waste diverted from landfill, based on EPA and WRAP food waste methodology. The Carbon Saved tab shows your total CO2, equivalents in trees planted and car-km avoided, and a breakdown by category.",
        category: "impact",
      },
      {
        id: "faq-24",
        question: "How do I reset my password?",
        answer: "On the login page, tap Forgot Password and enter your email address. A reset link is sent to your inbox. Follow the link to set a new password. If you do not receive the email, check your spam folder or try again after a few minutes.",
        category: "account",
      },
    ],

    guides: [
      {
        id: "getting-started",
        title: "Getting Started with Orrith - Cycle of Certainty",
        summary: "Set up your account, verify your email, and learn where each feature lives.",
        category: "getting-started",
        tags: ["getting-started"],
        sections: [
          {
            heading: "What is Orrith - Cycle of Certainty?",
            body: "Orrith - Cycle of Certainty connects Rwandan farmers, buyers, and communities into one system for trading produce, tracking crop prices, and measuring collective impact. Every feature works offline-first and is built around Rwanda's 30 districts.",
          },
          {
            heading: "Create Your Account",
            body: "Sign up with email, Google, or phone. During registration you set a username, verify your phone via OTP, and pick your location down to the cell level. Complete your profile to unlock selling and contributing to FarmPulse.",
          },
          {
            heading: "Navigate the App",
            body: "Farm is the produce marketplace. FarmPulse shows market prices. FarmMark tracks your impact. NorthStar is the live mission dashboard. Your Dashboard at /me links to Settings, Help, and all activity.",
          },
        ],
      },
      {
        id: "selling",
        title: "Selling on Farm",
        summary: "List produce with photos and pricing in RWF, handle offers, and track sales.",
        category: "marketplace",
        tags: ["marketplace", "getting-started"],
        sections: [
          {
            heading: "Create a Listing",
            body: "Tap Sell Now, then pick from 24 crop categories (Cereals, Legumes, Fruits, Vegetables, Tubers, and more). Upload up to 3 photos that get auto-compressed, set your price in RWF and quantity in kg. The system calculates an expiry date based on your crop type and storage conditions.",
          },
          {
            heading: "Manage Offers",
            body: "When a buyer makes an offer, it appears on your product page and in your Offer Manager at /my-offers. You can accept it which marks the product as sold, decline it, or continue negotiating through the chat that was created with the offer.",
          },
          {
            heading: "Edit, Hide, or Remove",
            body: "Use the three-dot menu on any listing to edit details, hide it from public view, or delete it. Your form drafts auto-save to your device so you can return and finish later. Products past their expiry date are automatically hidden from the marketplace.",
          },
        ],
      },
      {
        id: "buying",
        title: "Buying on Farm",
        summary: "Search nearby produce, filter by category, make offers, and chat with sellers.",
        category: "marketplace",
        tags: ["marketplace"],
        sections: [
          {
            heading: "Find Products",
            body: "Browse the Farm grid which shows products in a responsive layout. Use the search bar to find items by name, category, or location. Filter by any of the 24 crop categories using the horizontal chips, or tap Nearby to see only products within 5 km of you.",
          },
          {
            heading: "Make an Offer",
            body: "Open a product and tap Make Offer to propose your price in RWF. This instantly creates a private chat with the seller and sends your offer as a special message. You can negotiate back and forth, and the seller can accept or decline directly from the chat.",
          },
          {
            heading: "Save and Compare",
            body: "Tap the heart icon on any product to save it to your Wishlist for later. Each card shows a live countdown to expiry, the current wish count from other users, distance from your location, and the product state (fresh, dried, frozen, etc.).",
          },
        ],
      },
      {
        id: "farmpulse",
        title: "Using FarmPulse Market Data",
        summary: "View crop prices for 29 crops across all 30 Rwandan districts with trend analysis.",
        category: "market-data",
        tags: ["market-data"],
        sections: [
          {
            heading: "Read the Dashboard",
            body: "FarmPulse tracks 29 key Rwandan crops including maize, beans, rice, coffee, tea, cassava, and bananas. Each crop shows its last reported price in RWF, 7/30/90-day percentage changes, a supply trend indicator, and a market signal ranging from Strong Buy to Strong Avoid.",
          },
          {
            heading: "Use Filters and Charts",
            body: "Filter by district, growing season (Itumba, Impeshyi, Umuhindo, or Urugaryi), and time period. Each crop row has a sparkline chart showing price history. Predictions at 1, 3, and 6-month horizons show expected prices with color-coded confidence bars.",
          },
          {
            heading: "Contribute Prices",
            body: "Tap Contribute to submit a crop price you observed at your local market. Choose the crop, enter the price with a unit (kg, ton, bag, bundle, etc.), and select your district. A price is validated when 5 independent users report similar amounts within 7 days. You earn 1 IP per submission.",
          },
        ],
      },
      {
        id: "farmmark",
        title: "Tracking Impact with FarmMark",
        summary: "See your Impact Points, badge tier, leaderboard rank, and carbon savings.",
        category: "impact",
        tags: ["impact"],
        sections: [
          {
            heading: "Impact Points and Badges",
            body: "You earn 5 IP for listing a product, 1 IP per FarmPulse price submission, and 3 IP per star when rating. Your badge progresses through six tiers: Seedling, Sprout, Cultivator, Harvester, Guardian, and Legendary Steward at 1 million IP.",
          },
          {
            heading: "Leaderboards",
            body: "Two leaderboards rank the community: Best Sellers by product listings and Price Scouts by FarmPulse contributions. Filter results by this week, this month, or all-time to see who is most active.",
          },
          {
            heading: "Carbon Saved",
            body: "The Carbon Saved tab calculates CO2 prevented using 2.5 kg CO2 per kg of food waste diverted. It shows your total CO2 savings, equivalents in trees and car-kilometers, a progress ring toward your goal, a category breakdown chart, and a timeline of your recent impact actions.",
          },
        ],
      },
      {
        id: "northstar",
        title: "NorthStar Mission Hub",
        summary: "Live network stats, real-time impact feed, and the story behind Orrith - Cycle of Certainty.",
        category: "getting-started",
        tags: ["getting-started"],
        sections: [
          {
            heading: "Live Network Stats",
            body: "NorthStar displays real-time counts of community members, products listed, and impact events pulled directly from the database. A pulsing green dot confirms the data is live.",
          },
          {
            heading: "Impact Feed",
            body: "A live-updating feed shows the latest ratings from across the network as they happen. Each entry displays the user, action, quantity, and timestamp. The feed auto-refreshes so you always see the newest activity.",
          },
          {
            heading: "Quick Actions and Story",
            body: "Three buttons let you jump to List Produce, Browse Marketplace, or View Impact. The Feature Library section tells the origin story of each feature including Farm, FarmMark, and FarmPulse with detailed narratives.",
          },
        ],
      },
      {
        id: "messages",
        title: "Messages and Offers",
        summary: "Private chats created with offers. Includes offer bubbles and read receipts.",
        category: "marketplace",
        tags: ["marketplace"],
        sections: [
          {
            heading: "How Chats Start",
            body: "A chat is created automatically when you make an offer on a product. You can also start a chat from any product card using the message button. Each chat is linked to its product for context.",
          },
          {
            heading: "Offer Bubbles in Chat",
            body: "Offers appear as special colored bubbles inside the conversation. Pending offers show in yellow, accepted in green, and declined in red. The seller can accept or decline directly from the bubble. Accepted offers send a coordination message automatically.",
          },
          {
            heading: "Inbox and Notifications",
            body: "Your Inbox has two tabs: Messages showing all conversations sorted by most recent, and Notifications for system alerts like new offers and accepted deals. Unread messages show a green dot and bold text. Typing indicators appear in real time.",
          },
        ],
      },
      {
        id: "account-security",
        title: "Account and Security",
        summary: "Multiple sign-in methods, privacy toggles, data export, and account deletion.",
        category: "account",
        tags: ["account"],
        sections: [
          {
            heading: "Sign In and Profile",
            body: "Orrith supports four sign-in methods: email, username, phone number, or Google. During signup you verify your email, optionally verify your phone via OTP, and set your location down to the cell level. Your profile includes a display name, avatar, bio, and privacy-controlled fields.",
          },
          {
            heading: "Settings and Preferences",
            body: "Settings is organized into four sections: General (language and theme), Account (email, phone, profile access), Privacy and Data (export and reset), and Danger Zone (sign out and permanent account deletion).",
          },
          {
            heading: "Privacy and Data",
            body: "Each profile field — email, phone, avatar, location, and bio — has its own public/private toggle. You can export all your data as a downloadable JSON file at any time. Deleting your account permanently removes all your data and requires re-authentication if your session is old.",
          },
        ],
      },
      {
        id: "community",
        title: "Community Guidelines",
        summary: "Rules for honest listings, respectful interactions, and how to report violations.",
        category: "account",
        tags: ["account", "getting-started"],
        sections: [
          {
            heading: "Posting Standards",
            body: "Only list products with accurate descriptions and real photos. Misleading listings, inflated quantities, or fake images are flagged by the moderation system and can result in removal. Product photos are auto-compressed to keep the platform fast.",
          },
          {
            heading: "Trading and Conduct",
            body: "Negotiate fairly through the chat system. Harassment, discrimination, price manipulation, and scam attempts are not tolerated. The two-sided confirmation system on offers prevents fraud by requiring both buyer and seller to verify the deal.",
          },
          {
            heading: "Reporting",
            body: "Use the report button on any product to flag it for review. You can also open a support ticket from the Help Center. Our admin team reviews flagged content and can approve, remove, or restrict listings that violate community standards.",
          },
        ],
      },
    ],
  },

  rw: {
    categories: [
      { id: "getting-started", label: "Tangira Hano", desc: "Fungura konti, shakisha ibintu, hanyuma utangire gukoresha Orrith - Cycle of Certainty." },
      { id: "marketplace", label: "Isoko rya Farm", desc: "Shyira ibicuruzwa mu RWF, tanga ibiciro, negocia mu butumwa, ucunge ibyo wagurishije." },
      { id: "market-data", label: "Amakuru ya FarmPulse", desc: "Ibiciro by'imyaka byatanzwe n'abaturage mu turere 30 tw'u Rwanda hamwe n'imibare." },
      { id: "impact", label: "Ingaruka za FarmMark", desc: "Unguka Amanota y'Ingaruka, zamuka ku rutonde, kandi ukurikirane CO2 wakinguye." },
      { id: "account", label: "Konti n'Umutekano", desc: "Injira na imeyili, Google, cyangwa telefoni. Kuramo amakuru, tegeka ubuzima bwite, siba konti." },
    ],

    faq: [
      {
        id: "faq-1",
        question: "Nakora nte kugira ngo nshyire igicuruzwa ku isoko rya Farm?",
        answer: "Jya kuri Gura Nonaha, hitamo mu byiciro 24 by'imyaka, shyiraho amafoto ari mu 3, shyira igiciro mu RWF n'ingano mu kg, hanyuma uhitemo itariki irangiriraho. Igicuruzwa cyawe kigaragara ako kanya ku isoko rya Farm.",
        category: "marketplace",
      },
      {
        id: "faq-2",
        question: "Ibiciro no kugura bikora bite?",
        answer: "Fungura igicuruzwa ukande Tanga Igiciro kugira utange igiciro mu RWF. Ibyo bitangira ikiganiro cy'ibanga n'uwurisha aho mushobora kunegocia. Uwurisha nabyemera, igicuruzwa kigaragara ko cyagurishijwe.",
        category: "marketplace",
      },
      {
        id: "faq-3",
        question: "Akamenyetso ka Hafi Yawe ku isoko rya Farm ni iki?",
        answer: "Buto ya Hafi Yawe ikoresha GPS ya telefoni yawe kugaragaza gusa ibicuruzwa biri mu km 5. Ugomba kwemerera Orrith - Cycle of Certainty kumenya aho uherereye iyo bibajijwe. Ushobora no gutondeka ibicuruzwa ukurikije ibiri hafi kurusha ibindi.",
        category: "marketplace",
      },
      {
        id: "faq-4",
        question: "Nshobora kubika igicuruzwa kugira ngigure nyuma?",
        answer: "Yego, kanda ku mutima ku gikarita cy'igicuruzwa kugira ugishyire mu Bikunda Byawe. Ushobora kureba ibyo wabitse byose ku rupapuro rwa Wishlist. Gukureho ikigaragaza uburyo bwo gusubiza mu masegonda 6.",
        category: "marketplace",
      },
      {
        id: "faq-5",
        question: "Sisitemu y'igihe cy'irangira ikora ite ku bicuruzwa?",
        answer: "Iyo ushyize igicuruzwa, Orrith - Cycle of Certainty ibara igihe cy'kubika gishingiye ku bwoko bw'igihingwa, imimerere (bishya, byumwe, bikonje, n'ibindi), n'imimerere yo kubika. Igicuruzwa cyerekana igihe gisigaye. Ibicuruzwa byarenze igihe bihishwa mu buryo bwikora.",
        category: "marketplace",
      },
      {
        id: "faq-8",
        question: "FarmPulse ni iki kandi ibiciro bigera bite?",
        answer: "FarmPulse ni imbaho y'amakuru y'isoko igaragaza imyaka 29 mu turere 30 tw'u Rwanda. Abakoresha batanga ibiciro babona ku masoko y'aho, kandi igiciro kihamywa iyo abantu 5 batandukanye batanze ibiciro bisa mu minsi 7.",
        category: "market-data",
      },
      {
        id: "faq-9",
        question: "Natanga nte raporo y'igiciro kuri FarmPulse?",
        answer: "Fungura FarmPulse ukande Tanga. Hitamo ubwoko bw'igihingwa n'izina ryacyo, andika igiciro n'igipimo (kg, toni, umufuka, n'ibindi), uhitemo akarere kawe. Unguka Inota 1 ya Ingaruka kuri buri raporo kandi ushobora gutanga rimwe ku gihingwa ku munsi.",
        category: "market-data",
      },
      {
        id: "faq-10",
        question: "Ibimenyetso by'isoko muri FarmPulse bisobanura iki?",
        answer: "FarmPulse ibara ibimenyetso nka Gura Cyane, Gura, Tegereza, cyangwa Irinde bishingiye ku migendekere y'ibiciro y'iminsi 7, 30, na 90, impinduka z'iboneka, n'ibihe by'ihinga. Buri gihingwa kigaragaza igishushanyo cy'amateka y'ibiciro.",
        category: "market-data",
      },
      {
        id: "faq-11",
        question: "Amanota y'Ingaruka akora ate muri FarmMark?",
        answer: "Unguka IP 5 mu gushyira igicuruzwa, IP 1 kuri buri raporo ya FarmPulse, na IP 3 kuri buri nyenyeri mu gutanga amanota. Urwego rwawe ruzamuka kuva Seedling kugera Sprout, Cultivator, Harvester, Guardian, na Legendary Steward.",
        category: "impact",
      },
      {
        id: "faq-12",
        question: "FarmMark ifite amatonde y'abakomeye ahe?",
        answer: "FarmMark ifite amatonde 3: Abagurishije Neza bakurikije ibicuruzwa, Abatanze Cyane bakurikije ibitangwa, na Abashakashatsi b'Ibiciro bakurikije raporo za FarmPulse. Buri rutonde rushobora gushungurwa n'iki cyumweru, ukwezi, cyangwa igihe cyose.",
        category: "impact",
      },
      {
        id: "faq-13",
        question: "NorthStar ni iki?",
        answer: "NorthStar ni urupapuro rw'intego n'ingaruka za Orrith - Cycle of Certainty. Igaragaza imibare y'abanyamuryango, ibicuruzwa, ibitangwa, n'ibikorwa mu gihe nyacyo. Ifite kandi inkuru zibaho ku bitangwa, ibyo bisabwa, n'amanota mu gihe bibaye.",
        category: "getting-started",
      },
      {
        id: "faq-14",
        question: "Ubutumwa n'ibiganiro bikora bite?",
        answer: "Ibiganiro bitangira mu buryo bwikora iyo utanze igiciro ku gicuruzwa cyangwa ukasaba ibiciro bitangwa. Agasanduku kawe ka Inbox gafite amahuriro 2: Ubutumwa ku biganiro na Imenyesha ku butumwa bwa sisitemu. Ushobora gushakisha ibiganiro no kubona ibimenyetso byo gukora.",
        category: "marketplace",
      },
      {
        id: "faq-15",
        question: "Orrith - Cycle of Certainty ishyigikira uburyo buhe bwo kwinjira?",
        answer: "Ushobora kwinjira na imeyili n'ijambo ryibanga, izina ry'ukoresha n'ijambo ryibanga, nimero ya telefoni n'ijambo ryibanga, cyangwa konti ya Google. Kwiyandikisha birimo kwemeza telefoni binyuze muri OTP no guhitamo aho uherereye.",
        category: "account",
      },
      {
        id: "faq-16",
        question: "Nihindura nte ururimi rwa Orrith - Cycle of Certainty?",
        answer: "Jya mu Igenamiterere, hanyuma Guhitamo, uhitemo Icyongereza cyangwa Ikinyarwanda. Ihinduka ako kanya muri porogaramu yose. Ushobora kandi guhindura ururimi mbere yo kwinjira ukoresheje buto y'ururimi.",
        category: "account",
      },
      {
        id: "faq-17",
        question: "Nakuramo nte cyangwa nsibe amakuru yanjye?",
        answer: "Mu Igenamiterere munsi ya Gucunga Amakuru, kanda Kuramo Amakuru kugira umanure amakuru yawe yose nk'idosiye ya JSON. Gusiba konti, manuka kuri Danger Zone ukande Siba Konti. Ibyo bikuraho amakuru yawe yose burundu.",
        category: "account",
      },
      {
        id: "faq-18",
        question: "Ni ubuhe buryo bwo kurinda ubuzima bwite buhari?",
        answer: "Mu Igenamiterere munsi y'Ubuzima Bwite, ushobora guhindura umwirondoro wawe hagati ya rusange n'ibanga, guhitamo niba ugaragaza nimero ya telefoni, gutegeka gusangira aho uherereye, no gucunga uboneka mu gushakisha.",
        category: "account",
      },
      {
        id: "faq-19",
        question: "Nahindura nte cyangwa nhishe igicuruzwa nyuma yo kugishyira?",
        answer: "Fungura igicuruzwa cyawe ukande menyo itatu. Hitamo Hindura kugira uhindure amakuru, amafoto, cyangwa igiciro. Hitamo Hisha kugira ugifate mu ibanga cyangwa Siba kugira ugikureho burundu.",
        category: "marketplace",
      },
      {
        id: "faq-20",
        question: "Sisitemu y'igihe cy'irangira ikora ite ku bicuruzwa?",
        answer: "Iyo ushyize igicuruzwa, Orrith - Cycle of Certainty ibara igihe cy'kubika gishingiye ku bwoko bw'igihingwa, imimerere (bishya, byumye, bikonje, n'ibindi), n'imimerere yo kubika. Igicuruzwa cyerekana igihe gisigaye. Ibicuruzwa byarenze igihe bihishwa mu buryo bwikora.",
        category: "marketplace",
      },
      {
        id: "faq-21",
        question: "Nshobora gucuruza ibiciro byanjye byageneye?",
        answer: "Yego, fungura Gucunga Ibiciro kwawe ukande Subiza. Ibyo bisubiramo itariki y'intangiriro, bisubize imimerere kuri Biraboneka, bikakuraho uwari yarabisabye. Hari isaha 1 y'gutegereza hagati yo gusubiza kugira wirinde kurenga.",
        category: "marketplace",
      },
      {
        id: "faq-22",
        question: "Nshobora gurebera ibisabwa byanze ku bibanza byanjye?",
        answer: "Jya mu Gusangira Kwawe kugira ubone ibisabwa byose binjiye. Kuri buri gusaba ushobora gufungura ikiganiro, gusangira aho uri, kwemera gusaba, cyangwa kubihakanira. Nyuma yo gusubiza, kanda Byarangiye kugira urangize.",
        category: "marketplace",
      },
      {
        id: "faq-23",
        question: "Ingaruka za CO2 zibarwa zite muri FarmMark?",
        answer: "FarmMark igereranyo ko kg 2.5 za CO2 zikinguwe kuri buri kg y'ibiryo byacuruwe gufata ibicuro, bishingiye ku buryo bwa EPA na WRAP. Igice cya CO2 Yakinguwe kigaragaza CO2 yawe yose, ibigereranyo mu biti n'amakilometero ya modoka.",
        category: "impact",
      },
      {
        id: "faq-24",
        question: "Nihindura nte ijambo ryibanga?",
        answer: "Ku rupapuro rwo kwinjira, kanda Wibagiwe Ijambo Ryibanga wandike imeyili yawe. Uzoherezwa umurongo wo gusubiramo. Wukonere kugira ushyireho ijambo ryibanga rishya. Nutagera kuri iyo imeyili, reba mu spam cyangwa ugerageze nyuma y'iminota.",
        category: "account",
      },
    ],

    guides: [
      {
        id: "getting-started",
        title: "Tangira Orrith - Cycle of Certainty",
        summary: "Fungura konti yawe, emeza imeyili, kandi umenye aho buri kintu kiherereye.",
        category: "getting-started",
        tags: ["getting-started"],
        sections: [
          {
            heading: "Orrith - Cycle of Certainty ni iki?",
            body: "Orrith - Cycle of Certainty ihuza abahinzi, abaguzi, n'abaturage b'u Rwanda mu buryo bumwe bwo gucuruza imyaka, gukurikirana ibiciro, no gupima ingaruka. Buri kintu gikora offline kandi cyubatswe ku turere 30 tw'u Rwanda.",
          },
          {
            heading: "Kora Konti Yawe",
            body: "Iyandikishe na imeyili, Google, cyangwa telefoni. Mu kwiyandikisha ushyira izina ry'ukoresha, wemeze telefoni binyuze muri OTP, kandi uhiteme aho uri kugeza ku kagali. Uzuza umwirondoro kugira ubone ibintu byose.",
          },
          {
            heading: "Kugenda muri Porogaramu",
            body: "Farm ni isoko ry'imyaka. FarmPulse igaragaza ibiciro by'isoko. FarmMark ikurikirana ingaruka zawe. NorthStar ni imbaho y'intego. Dashboard yawe ku /me ihuza Igenamiterere, Ubufasha, n'ibikorwa byose.",
          },
        ],
      },
      {
        id: "selling",
        title: "Kugurisha kuri Farm",
        summary: "Shyira imyaka hamwe n'amafoto n'ibiciro mu RWF, cunga ibiciro, kandi ukurikirane ibyo wagurishije.",
        category: "marketplace",
        tags: ["marketplace", "getting-started"],
        sections: [
          {
            heading: "Kora Igicuruzwa",
            body: "Kanda Gura Nonaha, uhitemo mu byiciro 24 by'imyaka (Ibinyampeke, Ibinyamisogwe, Imbuto, Imboga, Ibinyabijumba, n'ibindi). Shyiraho amafoto 3, igiciro mu RWF n'ingano mu kg. Sisitemu ibara igihe irangiriraho gishingiye ku bwoko n'uko byabitswe.",
          },
          {
            heading: "Gucunga Ibiciro",
            body: "Iyo umugurzi atanze igiciro, kigaragara ku rupapuro rw'igicuruzwa no mu Gucunga Ibiciro. Ushobora kubyemera bigatuma igicuruzwa kigaragara ko cyagurishijwe, kubihakanira, cyangwa gukomeza kunegocia mu kiganiro.",
          },
          {
            heading: "Hindura, Hisha, cyangwa Kureho",
            body: "Koresha menyo itatu ku gicuruzwa icyo ari cyo cyose kugira uhindure amakuru, ugihishe ku bandi, cyangwa ugisibe. Ibyo wanditse ku fuome byibika mu buryo bwikora ku igikoresho cyawe kugira ubirangize nyuma.",
          },
        ],
      },
      {
        id: "buying",
        title: "Kugura kuri Farm",
        summary: "Shakisha imyaka y'hafi, shungura mu bwoko, tanga ibiciro, kandi uvugane n'abarishi.",
        category: "marketplace",
        tags: ["marketplace"],
        sections: [
          {
            heading: "Shaka Ibicuruzwa",
            body: "Reba imbaho ya Farm igaragaza ibicuruzwa. Koresha ishakiro kugira ubone ibintu ku izina, ubwoko, cyangwa aho biherereye. Shungura mu byiciro 24 ukoresheje chips, cyangwa kanda Hafi Yawe kugira ubone gusa ibicuruzwa biri mu km 5.",
          },
          {
            heading: "Tanga Igiciro",
            body: "Fungura igicuruzwa ukande Tanga Igiciro kugira utange igiciro mu RWF. Ibyo bitangira ikiganiro cy'ibanga n'uwurisha ako kanya. Mushobora kunegocia, kandi uwurisha ashobora kwemera cyangwa guhakanira mu kiganiro.",
          },
          {
            heading: "Bika no Kugereranya",
            body: "Kanda umutima ku gicuruzwa kugira ugibike mu Bikunda mu gihe ukigeraho nyuma. Buri gikarita kigaragaza igihe gisigaye, umubare w'abashaka kubigura, intera kuva aho uri, n'imimerere y'igicuruzwa.",
          },
        ],
      },
      {
        id: "farmpulse",
        title: "Gukoresha Amakuru ya FarmPulse",
        summary: "Reba ibiciro by'imyaka 29 mu turere 30 tw'u Rwanda hamwe n'isesengura ry'imigendekere.",
        category: "market-data",
        tags: ["market-data"],
        sections: [
          {
            heading: "Soma Imbaho",
            body: "FarmPulse ikurikirana imyaka 29 y'u Rwanda harimo ibigori, ibishyimbo, umuceri, ikawa, icyayi, imyumbati, n'amashaza. Buri gihingwa kigaragaza igiciro gishya mu RWF, impinduka z'iminsi 7/30/90, n'ikimenyetso cy'isoko kuva Gura Cyane kugeza Irinde.",
          },
          {
            heading: "Koresha Ibisasu n'Ibishushanyo",
            body: "Shungura mu karere, igihe cy'ihinga (Itumba, Impeshyi, Umuhindo, cyangwa Urugaryi), n'igihe. Buri gihingwa gifite igishushanyo cy'amateka. Ibiteganijwe ku kwezi 1, 3, na 6 bigaragaza ibiciro biteganijwe n'ibiciro by'ikizere.",
          },
          {
            heading: "Tanga Ibiciro",
            body: "Kanda Tanga kugira ushyire igiciro cy'igihingwa wabonye ku isoko ryaho. Hitamo igihingwa, andika igiciro n'igipimo, uhitemo akarere kawe. Igiciro kihamywa iyo abantu 5 batandukanye batanze ibiciro bisa mu minsi 7. Unguka IP 1 kuri buri raporo.",
          },
        ],
      },
      {
        id: "farmmark",
        title: "Gukurikirana Ingaruka na FarmMark",
        summary: "Reba Amanota y'Ingaruka, urwego rw'ibihembo, umwanya ku rutonde, no CO2 wakinguye.",
        category: "impact",
        tags: ["impact"],
        sections: [
          {
            heading: "Amanota n'Ibihembo",
            body: "Unguka IP 5 mu gushyira igicuruzwa, IP 1 kuri buri raporo ya FarmPulse, na IP 3 kuri buri nyenyeri mu gutanga amanota. Urwego rwawe ruzamuka kuva Seedling kugera Legendary Steward ku IP miliyoni 1.",
          },
          {
            heading: "Urutonde",
            body: "Amatonde 3 ahuriza abaturage: Abagurishije Neza hakurikijwe ibicuruzwa, Abatanze Cyane hakurikijwe ibitangwa, na Abashakashatsi b'Ibiciro hakurikijwe raporo za FarmPulse. Shungura ibyo iki cyumweru, ukwezi, cyangwa igihe cyose.",
          },
          {
            heading: "CO2 Yakinguwe",
            body: "Igice cya CO2 Yakinguwe kibara kg 2.5 za CO2 kuri buri kg y'ibiryo byacuruwe mu kuvainuza. Igaragaza CO2 yawe yose, ibigereranyo mu biti n'amakilometero ya modoka, uruziga rw'intego, igishushanyo cy'ubwoko, n'amateka y'ibikorwa byawe.",
          },
        ],
      },
      {
        id: "northstar",
        title: "Imbaho ya NorthStar",
        summary: "Imibare ya reseau mu gihe nyacyo, inkuru z'ingaruka, n'amateka ya Orrith - Cycle of Certainty.",
        category: "getting-started",
        tags: ["getting-started"],
        sections: [
          {
            heading: "Imibare y'Urubuga mu Gihe Nyacyo",
            body: "NorthStar igaragaza umubare w'abanyamuryango, ibicuruzwa, ibitangwa, n'ibikorwa by'ingaruka biva mu bubiko bw'amakuru. Agatookye katukura kahamya ko amakuru ari ay'ubu.",
          },
          {
            heading: "Inkuru z'Ingaruka",
            body: "Inkuru zisubirwamo zigaragaza ibitangwa, ibisabwa, n'amanota bishya ku rubuga uko bibaho. Buri kintu kigaragaza umukoresha, igikorwa, ingano, n'igihe. Zisubirwamo mu buryo bwikora.",
          },
          {
            heading: "Ibikorwa Byihuse n'Amateka",
            body: "Buto enye zigutwara kuri Shyira Imyaka, Reba Isoko, cyangwa Reba Ingaruka. Igice cy'Urutonde rw'Ibintu kisobanura amateka y'ibintu byose harimo Farm, FarmMark, na FarmPulse.",
          },
        ],
      },
      {
        id: "messages",
        title: "Ubutumwa n'Ibiciro",
        summary: "Ibiganiro by'ibanga bitangira n'ibiciro no gusaba. Bifite ibiciro bidasanzwe no kwemeza gusoma.",
        category: "marketplace",
        tags: ["marketplace"],
        sections: [
          {
            heading: "Uko Ibiganiro Bitangira",
            body: "Ikiganiro gitangira mu buryo bwikora iyo utanze igiciro ku gicuruzwa cyangwa usabye ibiciro bitangwa. Ushobora kandi gutangira ikiganiro kuva ku gikarita cy'igicuruzwa. Buri kiganiro kihuzwa n'igicuruzwa cyangwa ibiciro bitangwa kugira ubone ibintu byose.",
          },
          {
            heading: "Ibiciro mu Kiganiro",
            body: "Ibiciro bigaragara nk'amabuto yihariye mu kiganiro. Ibiciro bitegerejwe bigaragara mu umuhondo, ibyemewe mu icyatsi, n'ibihakanywe mu umutuku. Uwurisha ashobora kwemera cyangwa guhakanira mu kabuto. Ibyemewe byohereza ubutumwa bwo gutegura mu buryo bwikora.",
          },
          {
            heading: "Inbox n'Imenyesha",
            body: "Inbox yawe ifite amahuriro 2: Ubutumwa ugaragaza ibiganiro byose n'Imenyesha ku butumwa bwa sisitemu. Ubutumwa butasomwe bugaragaza agashami katukura. Ibimenyetso by'uko wandika bigaragara mu gihe nyacyo.",
          },
        ],
      },
      {
        id: "account-security",
        title: "Konti n'Umutekano",
        summary: "Uburyo butandukanye bwo kwinjira, guhindura ubuzima bwite, gukuramo amakuru, no gusiba konti.",
        category: "account",
        tags: ["account"],
        sections: [
          {
            heading: "Kwinjira n'Umwirondoro",
            body: "Orrith - Cycle of Certainty ishyigikira uburyo 4 bwo kwinjira: imeyili, izina ry'ukoresha, nimero ya telefoni, cyangwa Google. Mu kwiyandikisha wemeza imeyili, wemeze telefoni binyuze muri OTP, kandi ushyire aho uherereye kugeza ku kagali.",
          },
          {
            heading: "Igenamiterere n'Ibyo Uhitamo",
            body: "Igenamiterere rifite ibice 6: Guhitamo (isura, ururimi, aho uri), Imenyesha (imeyili, push, SMS), Ubuzima Bwite (umwirondoro, telefoni, gushakisha), Itumanaho (guhindura ururimi bwikora), Gucunga Amakuru (gukuramo, gusubiramo), na Akaga (gusohoka, gusiba).",
          },
          {
            heading: "Ubuzima Bwite n'Amakuru",
            body: "Buri gice cy'umwirondoro — imeyili, telefoni, ifoto, aho uherereye, n'ibisobanuro — gifite buto yacyo yo guhindura rusange/ibanga. Ushobora gukuramo amakuru yawe yose nk'idosiye ya JSON igihe icyo ari cyo cyose. Gusiba konti bikuraho amakuru yawe yose burundu.",
          },
        ],
      },
      {
        id: "community",
        title: "Amategeko y'Abaturage",
        summary: "Amategeko y'ibicuruzwa nyakuri, imyitwarire myiza, n'uko utanga raporo y'ibirenga.",
        category: "account",
        tags: ["account", "getting-started"],
        sections: [
          {
            heading: "Amahame yo Gushyira",
            body: "Shyira gusa ibicuruzwa n'ibitangwa bifite ibisobanuro nyakuri n'amafoto nyayo. Ibindimo bibeshya, ingano zisimbuwe, cyangwa amafoto y'ibinyoma bihishuzwa na sisitemu y'igenzura kandi bishobora gukurwaho.",
          },
          {
            heading: "Gucuruza n'Imyitwarire",
            body: "Negocia neza binyuze mu kiganiro. Gutoteza, ivangura, guhindura ibiciro, no kuriganya ntibikwemerewe. Sisitemu y'impande zombi kwemeza kuri ibiciro irinda ubwambure mu gufata ibintu byawe mu buryo bwikora.",
          },
          {
            heading: "Gutanga Raporo",
            body: "Koresha buto y'irego ku gicuruzwa cyangwa ibiciro bitangwa kugira ubimenyeshe ko bisuzumwe. Ushobora kandi gufungura impapuro z'ubufasha mu Bigo by'Ubufasha. Itsinda ryacu ry'abayobozi risuzuma ibindimo bikurikiranwa kandi rishobora kwemera, gukuraho, cyangwa guhagarika.",
          },
        ],
      },
    ],
  },
};

// ─── Public API ─────────────────────────────────────────────────

/** Get all help categories for a language */
export function getCategories(lang = "en") {
  return content[lang]?.categories || content.en.categories;
}

/** Get all FAQ items, optionally filtered by category */
export function getFaqItems(lang = "en", category = null) {
  const items = content[lang]?.faq || content.en.faq;
  if (!category) return items;
  return items.filter((f) => f.category === category);
}

/** Get all guide summaries */
export function getGuides(lang = "en", category = null) {
  const guides = content[lang]?.guides || content.en.guides;
  if (!category) return guides;
  return guides.filter((g) => g.category === category);
}

/** Get a single guide by ID */
export function getGuide(id, lang = "en") {
  const guides = content[lang]?.guides || content.en.guides;
  return guides.find((g) => g.id === id) || null;
}

/** Search FAQ + guides by free text query */
export function searchHelp(query, lang = "en") {
  if (!query || !query.trim()) return { faq: [], guides: [] };
  const q = query.trim().toLowerCase();
  const faq = (content[lang]?.faq || content.en.faq).filter(
    (f) => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)
  );
  const guides = (content[lang]?.guides || content.en.guides).filter(
    (g) =>
      g.title.toLowerCase().includes(q) ||
      g.summary.toLowerCase().includes(q) ||
      g.sections.some((s) => s.heading.toLowerCase().includes(q) || s.body.toLowerCase().includes(q))
  );
  return { faq, guides };
}



